import { Box, Button, Chip, Typography } from '@mui/material';
import styled from '@mui/material/styles/styled';
import { SUBSCRIPTION_STATUS } from '../constants';

const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})(({ theme, highlighted }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: '12px',
  border: highlighted ? `2px solid ${theme.palette.secondary.main}` : `1px solid ${theme.palette.grey[200]}`,
  padding: '24px',
  minHeight: '240px',
}));

const getCardAction = ({
  planId,
  variantId,
  currentPlanId,
  currentVariantId,
  subscriptionStatus,
  isCustom,
  highlighted,
  labels,
}) => {
  if (isCustom) {
    return { type: 'button', label: labels.letsTalk, variant: 'outlined', color: 'secondary' };
  }

  // "Current plan" text only when ACTIVE and both planId + variantId match
  const isCurrentPlan =
    subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
    planId === currentPlanId &&
    variantId === currentVariantId;

  if (isCurrentPlan) {
    return { type: 'text', label: labels.currentPlan };
  }

  // All other cases: "Continue with" / "Upgrade" button
  return {
    type: 'button',
    label: labels.continueWith,
    variant: highlighted ? 'contained' : 'outlined',
    color: 'secondary',
  };
};

const PricingCard = ({
  planId,
  variantId,
  name,
  price,
  period,
  description,
  currentPlanId,
  currentVariantId,
  subscriptionStatus,
  highlighted,
  isCustom,
  isPrivate,
  labels,
  onSelect,
  // TODO: uncomment when downgrade feature is enabled
  // onDowngrade,
  onLetsTalk,
}) => {
  const cardAction = getCardAction({
    planId,
    variantId,
    currentPlanId,
    currentVariantId,
    subscriptionStatus,
    isCustom,
    highlighted,
    labels,
  });

  const handleClick = () => {
    if (isCustom) {
      onLetsTalk?.();
    } else {
      onSelect?.(planId, variantId);
    }
  };

  return (
    <Card highlighted={highlighted}>
      <Box>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
          <Typography variant="h6" fontWeight={700} color={highlighted ? 'secondary.main' : 'accent.900'}>
            {name}
          </Typography>
          {isPrivate && (
            <Chip
              label={labels.preparedForYou}
              size="small"
              sx={{
                backgroundColor: 'violet.100',
                color: 'violet.900',
                fontWeight: 600,
                fontSize: '11px',
                height: '20px',
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="baseline" gap="4px" mt="4px">
          {isCustom ? (
            <Typography variant="body2" color="accent.700">
              {price ? (
                <>
                  {labels.from} <b>{price}</b>
                </>
              ) : (
                labels.custom
              )}
            </Typography>
          ) : (
            <Typography variant="body2" color="accent.700">
              <b>{price}</b> {period}
            </Typography>
          )}
        </Box>
        <Typography
          variant="body2"
          color="accent.500"
          mt="20px"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </Box>
      {cardAction.type === 'text' ? (
        <Typography
          variant="body2"
          fontWeight={600}
          color="secondary.main"
          textAlign="center"
          mt="20px"
          py="6px"
        >
          {cardAction.label}
        </Typography>
      ) : (
        <Button
          fullWidth
          variant={cardAction.variant}
          color={cardAction.color}
          disabled={cardAction.disabled}
          onClick={handleClick}
          sx={{ borderRadius: '24px', textTransform: 'none', mt: '20px' }}
        >
          {cardAction.label}
        </Button>
      )}
    </Card>
  );
};

export default PricingCard;
